import { Flame, Zap, Shield, Crown, CheckCircle } from "lucide-react";

const MILESTONES = [
  { days: 3, label: "3-Day Spark", icon: Zap, color: "from-yellow-400 to-amber-500", ring: "ring-yellow-300" },
  { days: 7, label: "7-Day Flow", icon: Flame, color: "from-orange-400 to-red-500", ring: "ring-orange-300" },
  { days: 14, label: "14-Day Warrior", icon: Shield, color: "from-blue-400 to-indigo-500", ring: "ring-blue-300" },
  { days: 30, label: "30-Day Legend", icon: Crown, color: "from-purple-400 to-pink-500", ring: "ring-purple-300" },
];

export function StreakTracker({ streak }) {
  if (!streak) return null;
  return (
    <div data-testid="session-streak" className="mb-8 max-w-3xl mx-auto">
      <div className="rounded-2xl bg-white/80 backdrop-blur-sm border border-emerald-100 shadow-sm p-4">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="relative">
              <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                streak.current_streak > 0
                  ? 'bg-gradient-to-br from-amber-400 to-orange-500 shadow-lg shadow-amber-500/25'
                  : 'bg-gray-100'
              }`}>
                <Flame className={`w-7 h-7 ${streak.current_streak > 0 ? 'text-white' : 'text-gray-400'}`} />
              </div>
              {streak.current_streak > 0 && (
                <span className="absolute -top-1.5 -right-1.5 w-6 h-6 rounded-full bg-emerald-500 text-white text-[10px] font-black flex items-center justify-center shadow-sm">
                  {streak.current_streak}
                </span>
              )}
            </div>
            <div>
              <div className="flex items-baseline gap-1.5">
                <span data-testid="streak-count" className="text-2xl font-black text-gray-900">{streak.current_streak}</span>
                <span className="text-sm font-semibold text-gray-500">day streak</span>
              </div>
              <p className="text-xs text-gray-400 mt-0.5">
                {streak.current_streak > 0
                  ? streak.next_milestone
                    ? `${streak.next_milestone.days - streak.current_streak} more day${streak.next_milestone.days - streak.current_streak !== 1 ? 's' : ''} to ${streak.next_milestone.label}`
                    : 'All milestones achieved!'
                  : 'Complete a session to start your streak'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-5">
            <div className="text-center">
              <p className="text-lg font-black text-gray-900">{streak.longest_streak}</p>
              <p className="text-[9px] text-gray-400 uppercase tracking-wider font-bold">Best</p>
            </div>
            <div className="w-px h-8 bg-gray-200" />
            <div className="text-center">
              <p className="text-lg font-black text-gray-900">{streak.total_sessions}</p>
              <p className="text-[9px] text-gray-400 uppercase tracking-wider font-bold">Sessions</p>
            </div>
            <div className="w-px h-8 bg-gray-200" />
            <div className="text-center">
              <p className="text-lg font-black text-gray-900">{streak.total_days}</p>
              <p className="text-[9px] text-gray-400 uppercase tracking-wider font-bold">Days</p>
            </div>
          </div>
          <div data-testid="milestone-badges" className="flex items-center gap-2">
            {MILESTONES.map((m) => {
              const achieved = streak.milestones?.some(ms => ms.days === m.days);
              const IconComp = m.icon;
              return (
                <div
                  key={m.days}
                  data-testid={`milestone-${m.days}`}
                  title={achieved ? `${m.label} - Achieved!` : `${m.label} - ${m.days}-day streak required`}
                  className={`relative w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                    achieved
                      ? `bg-gradient-to-br ${m.color} shadow-md ring-2 ${m.ring} ring-offset-1`
                      : 'bg-gray-100 opacity-40'
                  }`}
                >
                  <IconComp className={`w-5 h-5 ${achieved ? 'text-white' : 'text-gray-400'}`} />
                  {achieved && (
                    <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-white flex items-center justify-center">
                      <CheckCircle className="w-3 h-3 text-emerald-500" />
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
